import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FileText, Bell, UserCheck } from 'lucide-react';
import { BiSolidArrowToRight as ArrowR } from "react-icons/bi";
import { FaWifi } from 'react-icons/fa'; // Importing the new icon


const pulse = keyframes`
  0% {
    transform: rotate(90deg) scale(1);
    opacity: 0.7;
  }
  50% {
    transform: rotate(90deg) scale(1.2);
    opacity: 1;
  }
  100% {
    transform: rotate(90deg) scale(1);
    opacity: 0.7;
  }
`;

const pulseMobile = keyframes`
  0% {
    transform: rotate(180deg) scale(1);
    opacity: 0.7;
  }
  50% {
    transform: rotate(180deg) scale(1.2);
    opacity: 1;
  }
  100% {
    transform: rotate(180deg) scale(1);
    opacity: 0.7;
  }
`;

// Keyframe animation to fade in and slide up
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WifiArrow = styled(FaWifi)`
  margin: 1rem auto;
  font-size: 2rem;
  color: #1e3a8a;
  margin-top: 150px;
  animation: ${pulse} 1.2s ease-in-out infinite;
  transform: rotate(90deg); /* Default: face right on desktop */

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0.5rem auto;
    animation: ${pulseMobile} 1.2s ease-in-out infinite;
    transform: rotate(180deg); /* Face down on mobile */
  }
`;

// Styled component for the section
const Section = styled.section`
  width: 95%;
  margin-top: 2.2rem;
  padding: 2rem;
  background: linear-gradient(180deg, #ffffff 0%, #f9fbff 100%);
  border: 1px solid #d7e6f8;
  border-radius: 1.5rem;
  text-align: center;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.1);
  position: relative; /* Ensure stacking */

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
    margin-top: 2rem; /* Reduce spacing on smaller screens */
  }
`;



// Styled component for the title
const Title = styled.h2`
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.6rem, 3.6vw, 2.1rem);
  font-weight: 700;
  margin-bottom: 1rem;
  color: #0b3d73;
  letter-spacing: -0.02em;
  text-wrap: balance;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 1px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const Step = styled.div`
  flex: 1 1 calc(33.333% - 1rem);
  max-width: 300px;
  background: #ffffff;
  color: #333;
  border-radius: 1rem;
  border: 1px solid #e2ecf8;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  animation: ${fadeInUp} 0.6s ease forwards;
  padding: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    flex: 1 1 100%;
    max-width: 100%;
    padding: 1rem;
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background-color: #eaf2ff;
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 1rem;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const StepTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  margin-bottom: 1rem;
  color: #0b3d73;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const StepDescription = styled.p`
  font-size: 1rem;
  color: #4b5f73;
  line-height: 1.5;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const StepNumber = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #1f6fd1;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export default function HowItWorks() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  // Use IntersectionObserver to trigger animation when section is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Once the animation is triggered, stop observing
        }
      },
      { threshold: 0.2 } // Trigger when 20% of the section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <Section ref={sectionRef}>
      <Title>How to Find Trusted Local Tradespeople — in Minutes</Title>
      <StepsContainer>
        <Step isVisible={isVisible} delay={0.2}>
          <StepNumber>1</StepNumber>
          <IconWrapper>
            <FileText size={40} color="#1890ff" />
          </IconWrapper>
         <StepTitle>Post a Job to Find Trusted Tradespeople Near You</StepTitle>
        <StepDescription>
        Post your job in minutes and get free quotes from verified local electricians, plumbers, handypeople, and builders. We’ll quickly match you with trusted tradespeople near you—fast, reliable, and hassle-free.
        </StepDescription>
        </Step>
        <WifiArrow />
        <Step isVisible={isVisible} delay={0.4}>
          <StepNumber>2</StepNumber>
          <IconWrapper>
            <Bell size={40} color="#1890ff" />
          </IconWrapper>
          <StepTitle>Instant Alerts Sent to Verified Local Tradespeople</StepTitle>
            <StepDescription>
            We instantly alert verified local tradespeople near your job — from carpenters and painters to roofers — ensuring your post reaches the right professionals fast.
            </StepDescription>
        </Step>
        <WifiArrow />
        <Step isVisible={isVisible} delay={0.6}>
          <StepNumber>3</StepNumber>
          <IconWrapper>
            <UserCheck size={40} color="#1890ff" />
          </IconWrapper>
          <StepTitle>Choose the Best Local Professional for the Job</StepTitle>
          <StepDescription>
          Compare trusted local tradespeople by ratings, experience, and quotes. From builders and gardeners to plasterers, you stay in control and choose the right professional for your job.
          </StepDescription>
      
        </Step>
       
      </StepsContainer>
    </Section>
  );
}
